import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import FormApplicantGenerality from '../generality';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { AxiosResponse } from 'axios';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useLocation } from 'react-router-dom';
import { PersonalDataVM } from 'shell/view-models/personal-data';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';

interface AccordionApplicantGeneralityProps {
  isOpen: boolean;
  formContext: FormikProps<RegisterOfficeVM>;
  disabled?: boolean;
  applicantIsAccountholder?: boolean;
  setOpenModalNewPerson: (value: boolean) => void;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
}

interface RequestPersonPractice {
  data: {
    tipoRegistro: string | undefined;
    idAnnoPratica: number | undefined;
    progressivoPratica: number | undefined;
    datiRichiesta: {
      ricercaNazionale: string;
    };
    datiNascita:
      | {
          senzaGiorno: string | undefined;
          senzaGiornoMese: string | undefined;
          dataEvento: string | undefined;
        }
      | undefined;
    criteriRicerca: {
      codiceFiscale: string | undefined;
      cognome: string | undefined;
      nome: string | undefined;
      senzaCognome: string | undefined;
      senzaNome: string | undefined;
    };
  };
}

const AccordionApplicantGeneralityComponent = ({
  isOpen,
  formContext,
  disabled,
  applicantIsAccountholder,
  typeRegister,
  yearId,
  progressive,
}: AccordionApplicantGeneralityProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(isOpen);

  const [, getPerson] = useHttpFetch<HttpResponse<ResponseAnprData>, RegisterOfficeVM>(
    `${__PERSON_API_URL__}/persona/pratiche/ricerca-richiedente`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponse<ResponseAnprData>>) =>
        transformerPersonToRegisterOffice(response, true),
    }
  );

  const state = useLocation().state as RequestPersonPractice;

  useEffect(() => {
    if (isOpen) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      state?.data?.criteriRicerca?.codiceFiscale ||
      state?.data?.criteriRicerca?.nome ||
      state?.data?.criteriRicerca?.cognome
    ) {
      const data = state?.data;
      (
        getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, RegisterOfficeVM>
      ).then((response) => {
        const castedResponse = response as RegisterOfficeVM;
        formContext.setValues({
          ...(formContext.values || {}),
          applicantIsAccountholder: false,
          applicantData: castedResponse.applicantData,
        });
      });
    }
  }, [state]);

  const onChange = useEventCallback(
    (key: keyof PersonalDataVM, value: PersonalDataVM[keyof PersonalDataVM]) => {
      formContext.setFieldValue('applicantData', {
        ...(formContext.values.applicantData || {}),
        [key]: value,
      });
    }
  );

  const onChangeFields = useEventCallback((values: PersonalDataVM) => {
    formContext.setFieldValue('applicantData', {
      ...(formContext.values.applicantData || {}),
      ...values,
    });
  });

  const onSearch = useEventCallback(() => {
    const hasSearchBirthFilters =
      !!formContext.values.applicantData?.birthdayChoice &&
      !!formContext.values.applicantData?.birthday;

    const data = {
      tipoRegistro: typeRegister,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      datiRichiesta: {
        ricercaNazionale: '1', // fixed
      },
      datiNascita: hasSearchBirthFilters
        ? {
            senzaGiorno:
              !formContext.values.applicantData?.birthdayChoice?.includes('day') &&
              formContext.values.applicantData?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !formContext.values.applicantData?.birthdayChoice?.includes('day') &&
              !formContext.values.applicantData?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            dataEvento:
              formContext.values.applicantData?.birthday?.toFormat('yyyy-MM-dd') || undefined,
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: formContext.values.applicantData?.fiscalCode || undefined,
        cognome: formContext.values.applicantData?.surname || undefined,
        nome: formContext.values.applicantData?.name || undefined,
        senzaCognome: formContext.values.applicantData?.withoutSurname ? '1' : undefined,
        senzaNome: formContext.values.applicantData?.withoutName ? '1' : undefined,
      },
    };

    (getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, RegisterOfficeVM>).then(
      (response) => {
        const castedResponse = response as RegisterOfficeVM;

        // se nessuna risposta non aprire nuova modale ma permettere la modifica in pagina
        // if (castedResponse.applicantData?.openModalNewPerson) {
        //   // open modal registrazione nuova persona
        //   setOpenModalNewPerson(true);
        //   return;
        // }

        formContext.setValues({
          ...(formContext.values || {}),
          applicantData: castedResponse.applicantData,
        });
      }
    );
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (!applicantIsAccountholder) setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls={`panel-register-office-Generalità richiedente-content`}
        id={`panel-register-office-Generalità richiedente-header`}
        expandIcon={<ExpandMore />}
      >
        <Typography>Generalità richiedente</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormApplicantGenerality
          onChangeFields={onChangeFields}
          onChange={onChange}
          values={formContext.values.applicantData || {}}
          onSearch={onSearch}
          disabled={disabled}
          typeRegister={typeRegister}
          yearId={yearId}
          progressive={progressive}
          isApplicant
          url={`${__PERSON_API_URL__}/persona/pratiche/ricerca-richiedente`}
          errors={formContext.errors.applicantData as FormikErrors<PersonalDataVM>}
          touched={formContext.touched.applicantData as unknown as FormikTouched<PersonalDataVM>}
        />
      </AccordionDetails>
    </Accordion>
  );
};

AccordionApplicantGeneralityComponent.displayName =
  'Accordion Register Office Applicant generality component';

const AccordionApplicantGenerality = memo(AccordionApplicantGeneralityComponent);

export default AccordionApplicantGenerality;
